import { useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useGateways } from '../../../hooks/useGateways';
import { supabase } from '../../../services/supabaseClient';

/**
 * Adapter so ETL flow editor uses Chillverify auth + gateways.
 */
export const useEtlAuth = () => {
  const { user, signOut, loading: authLoading, isAuthenticated } = useAuth();
  const { gateways, isLoading: gatewaysLoading, refetch } = useGateways();

  const logout = useCallback(async () => {
    await signOut();
    return { error: null };
  }, [signOut]);

  const saveGateway = useCallback(async (gatewayData) => {
    if (!user?.id) {
      return { error: { message: 'Not authenticated' } };
    }

    const dataToSave = {
      ...gatewayData,
      user_id: user.id,
    };

    if (gatewayData.id) {
      const { data, error } = await supabase
        .from('gateways')
        .update({
          ...dataToSave,
          updated_at: new Date().toISOString(),
        })
        .eq('id', gatewayData.id)
        .select()
        .single();

      if (!error) {
        refetch();
      }
      return { data, error };
    }

    const { data, error } = await supabase
      .from('gateways')
      .insert([dataToSave])
      .select()
      .single();

    if (!error) {
      refetch();
    }
    return { data, error };
  }, [user, refetch]);

  const removeGateway = useCallback(async (gatewayId) => {
    const { data, error } = await supabase
      .from('gateways')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', gatewayId)
      .select()
      .single();

    if (!error) {
      refetch();
    }
    return { data, error };
  }, [refetch]);

  const refreshGateways = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    user,
    session: null,
    gateways: gateways || [],
    loading: authLoading || gatewaysLoading,
    logout,
    isAuthenticated,
    saveGateway,
    removeGateway,
    refreshGateways,
  };
};

export default useEtlAuth;
